import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { PageHeader, Chip } from "../components/ui-bits";
import { StockOverviewCard } from "../components/StockOverviewCard";
import { SpendChartCard } from "../components/SpendChartCard";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import {
  Users, Layers, Shirt, ClipboardList, DollarSign, CalendarClock,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="card-dark p-6 relative overflow-hidden hover-lift">
    <div className="flex items-start justify-between mb-6">
      <div className="label-caps">{label}</div>
      <Icon size={18} strokeWidth={1.5} style={{ color: "var(--text-primary)" }}/>
    </div>
    <div className="font-serif text-4xl font-light" style={{ color: "var(--text-primary)" }}>{value}</div>
    {sub && <div className="text-xs mt-2 uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{sub}</div>}
  </div>
);

const GOLD = "#0A0A0A";

// Brand-specific hero shown only when the active brand uploaded a background.
// The MMS Studio logo NEVER changes based on the brand — this is the brand
// identity block sitting BELOW the platform chrome. A theme-aware overlay
// keeps text and controls readable at any background lightness.
function BrandHero({ brand, themeMode }) {
  const bgId = brand?.background_file_id;
  const logoId = brand?.logo_file_id;
  if (!bgId && !logoId) return null;
  const overlay = themeMode === "dark"
    ? "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 100%)"
    : "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.75) 100%)";
  return (
    <div
      className="relative overflow-hidden mb-6"
      style={{
        height: 220,
        borderRadius: "var(--radius)",
        border: "1px solid var(--border)",
        background: "var(--surface-tertiary)",
      }}
      data-testid="brand-hero"
    >
      {bgId && (
        <img
          src={`${API}/files/${bgId}`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="brand-hero-background"
        />
      )}
      <div className="absolute inset-0" style={{ background: overlay }}/>
      <div className="absolute inset-0 flex items-end p-8 gap-4">
        {logoId && (
          <div
            className="flex items-center justify-center p-3"
            style={{
              width: 72, height: 72,
              background: themeMode === "dark" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.95)",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "var(--radius)",
            }}
          >
            <img
              src={`${API}/files/${logoId}?variant=thumb`}
              alt={brand.name}
              className="max-w-full max-h-full object-contain"
              data-testid="brand-hero-logo"
            />
          </div>
        )}
        <div className="min-w-0">
          <div className="label-caps mb-1" style={{ color: "var(--text-primary)" }}>Brand</div>
          <div className="font-serif text-3xl leading-tight" style={{ color: "var(--text-primary)" }} data-testid="brand-hero-name">
            {brand.name}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { activeBrand, themeMode } = useBrand();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(()=>{});
  }, []);

  const ordersData = (stats?.orders_by_status || []).map(o => ({ name: o._id, value: o.count, total: o.total }));
  const tasksData = (stats?.tasks_by_status || []).map(t => ({ name: t._id, value: t.count }));

  return (
    <div data-testid="dashboard-page">
      <BrandHero brand={activeBrand} themeMode={themeMode}/>
      <PageHeader
        eyebrow={`Welcome, ${user?.name}`}
        title="Studio Overview"
        description="Real-time visibility across your brands, production floor, and revenue."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi icon={Users} label="Clients" value={stats?.clients ?? "—"} sub="active brands"/>
        <Kpi icon={Layers} label="Collections" value={stats?.collections ?? "—"} sub="all seasons"/>
        <Kpi icon={Shirt} label="Garments" value={stats?.garments ?? "—"} sub="in catalogue"/>
        <Kpi icon={ClipboardList} label="Orders" value={stats?.orders ?? "—"} sub="total"/>
      </div>
      <StockOverviewCard/>
      <SpendChartCard/>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="label-caps">Revenue</div>
            <DollarSign size={18} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          </div>
          <div className="font-serif text-4xl font-light">€ {(stats?.revenue ?? 0).toLocaleString()}</div>
          <div className="text-xs text-[#737373] mt-2 uppercase tracking-wider">Confirmed & shipped</div>
        </div>
        <div className="card-dark p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div className="label-caps">Orders by Status</div>
            <ClipboardList size={18} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          </div>
          {ordersData.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-xs uppercase tracking-widest text-[#737373]">
              No orders yet
            </div>
          ) : (
            <div style={{ width: "100%", height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ordersData}>
                  <XAxis dataKey="name" tick={{ fill: "#525252", fontSize: 11 }} axisLine={{ stroke: "#333" }}/>
                  <YAxis tick={{ fill: "#525252", fontSize: 11 }} axisLine={{ stroke: "#333" }}/>
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E5E5", color: "#fff" }}/>
                  <Bar dataKey="value" fill={GOLD}>
                    {ordersData.map((_, i) => <Cell key={i} fill={GOLD}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {user?.role !== "client" && (
        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="label-caps">Production tasks</div>
            <CalendarClock size={18} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["todo","in_progress","done","blocked"].map((s) => {
              const found = tasksData.find(t => t.name === s);
              return (
                <div key={s} className="border border-[#E5E5E5] p-4">
                  <div className="label-caps mb-2">{s.replace("_"," ")}</div>
                  <div className="font-serif text-2xl">{found?.value ?? 0}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
